import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchLeads } from "../../store/lead"
import { fetchQuotes, createQuote } from "../../store/quote"
import LeadQuoteList from "../ui/LeadQuoteList"
import ReactPaginate from "react-paginate"
import {
  ArrowBigLeftDashIcon,
  ArrowBigRightDashIcon,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react"
import Spinner from "../Spinner"
import { formatPhoneNumber } from "../../util"
import "../styles/AdminQuotePage.css"

function AdminQuotePage() {
  const dispatch = useDispatch()
  const { leads } = useSelector((state) => state.lead)
  const quotes = useSelector((state) => state.quotes.quotes)
  const [selectedLead, setSelectedLead] = useState(null)
  const [selectedQuote, setSelectedQuote] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedLeads, setExpandedLeads] = useState(new Set())
  const itemsPerPage = 10

  useEffect(() => {
    dispatch(fetchLeads())
    dispatch(fetchQuotes())
  }, [dispatch])

  const toggleLeadExpansion = (leadId) => {
    setExpandedLeads((prevExpandedLeads) => {
      const newExpandedLeads = new Set(prevExpandedLeads)
      if (newExpandedLeads.has(leadId)) {
        newExpandedLeads.delete(leadId)
      } else {
        newExpandedLeads.add(leadId)
      }
      return newExpandedLeads
    })
  }

  const handleLeadClick = (lead) => {
    setSelectedLead(lead)
    const associatedQuote = quotes.find((quote) => quote.leadId === lead.id)
    setSelectedQuote(associatedQuote || null)
  }

  const handleSaveChanges = async (updatedQuote) => {
    if (updatedQuote.id) {
      await dispatch(
        updateQuote({ id: updatedQuote.id, updatedData: updatedQuote })
      )
    } else {
      await dispatch(createQuote(updatedQuote))
    }
    setSelectedQuote(updatedQuote)
    dispatch(fetchQuotes())
  }

  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1)
  }

  const startIndex = (currentPage - 1) * itemsPerPage
  const selectedLeads = leads.slice(startIndex, startIndex + itemsPerPage)
  const pageCount = Math.ceil(leads.length / itemsPerPage)

  return (
    <div className="adminLeadsPageContainer">
      <h1 className="adminLeadsPageHeader">Quotes</h1>
      <h1 className="adminLeadsPageHeader">Select A Lead To View Quote</h1>
      <div className="adminLeadsTableWrapper">
        {leads.length ? (
          <table className="adminLeadsTable">
            <thead>
              <tr>
                <th>ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Address</th>
                <th>City</th>
                <th>Zip Code</th>
                <th>Service</th>
                <th>Appt. Date</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {selectedLeads.map((lead) => {
                const isExpanded = expandedLeads.has(lead.message)
                const words = lead.message.split(" ")
                const shouldTruncate = words.length > 3
                const formattedPhone = formatPhoneNumber(lead.phone)
                const displayedMessage =
                  isExpanded || !shouldTruncate
                    ? lead.message
                    : words.slice(0, 3).join(" ") + "..."

                return (
                  <tr
                    key={lead.id}
                    onClick={() => handleLeadClick(lead)}
                    className="singleLeadLine"
                  >
                    <td>{lead.id}</td>
                    <td>{lead.firstName}</td>
                    <td>{lead.lastName}</td>
                    <td>{lead.email}</td>
                    <td>{formattedPhone}</td>
                    <td>{lead.address}</td>
                    <td>{lead.city}</td>
                    <td>{lead.zipCode}</td>
                    <td>{lead.service}</td>
                    <td>
                      {lead.preferredAppointmentDateTime
                        ? new Date(
                            lead.preferredAppointmentDateTime
                          ).toLocaleString()
                        : "N/A"}
                    </td>
                    <td>
                      <span>{displayedMessage}</span>
                      {shouldTruncate && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleLeadExpansion(lead.message)
                          }}
                          className="readMoreButton"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronsLeft size={20} />
                              <span>Read Less</span>
                            </>
                          ) : (
                            <>
                              <span>Read More</span>
                              <ChevronsRight size={20} />
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        ) : (
          <Spinner />
        )}
        <ReactPaginate
          previousLabel={<ArrowBigLeftDashIcon size={24} />}
          nextLabel={<ArrowBigRightDashIcon size={24} />}
          pageCount={pageCount}
          onPageChange={handlePageClick}
          containerClassName="pagination"
          activeClassName="active"
        />
      </div>
      {selectedLead && (
        <>
          <LeadQuoteList
            leadId={selectedLead.id}
            onSelectQuote={setSelectedQuote}
            selectedQuote={selectedQuote}
          />
        </>
      )}
    </div>
  )
}

export default AdminQuotePage
